/**
 * Реестр ярусов принятой связи «фича NGIS ↔ контур» — ОДИН на весь экран.
 *
 * Зачем отдельный модуль
 * ----------------------
 *
 * Ярус решает три разные вещи сразу: какой подписью его назвать, какой обводкой
 * нарисовать полигон и каким образцом показать его в легенде. Пока это были три
 * независимых места (словарь в `mapModel`, три слоя в `MapCanvas`, тернарник в
 * `MapScreen`), добавление яруса не ломало НИЧЕГО — оно просто не доезжало:
 *
 *   * `merged_into` и `m_to_n` приняты в канон и приезжают в каждой выдаче
 *     карты, но слоя обводки у них не было, и 249 фич не имели на карте ни
 *     одной собственной черты;
 *   * легенда красила их образцом яруса `group` — тернарником «auto → scored →
 *     иначе group», — и подписывала подписью, которая утверждает ОБРАТНОЕ:
 *     «контур собран из нескольких фич». У merge всё наоборот: одна фича
 *     показывает несколько контуров.
 *
 * Поэтому здесь один список, из которого выводится всё остальное: подписи,
 * порядок, слои обводки, образцы легенды. Ярус, добавленный сюда, появляется
 * во всех трёх местах сам. Ярус, НЕ добавленный сюда, не исчезает: у карты есть
 * слой «незнакомый ярус», а у подписи — форма «неизвестно: код». Молча пропасть
 * ярус не может ни при каком развитии.
 *
 * Тесты этого экрана идут по реестру, а не по своему списку: тест со своим
 * списком воспроизводил бы ровно тот дефект, который проверяет.
 */

import type { FilterSpecification } from "maplibre-gl";
import { defineMessages } from "../../i18n/define";
import { tr } from "../../i18n/runtime";

const m = defineMessages({
  tierAuto: {
    id: "datamap.tier.auto",
    defaultMessage: "автоматический",
    description:
      "Ярус принятой связи «фича NGIS ↔ контур» (подпись в легенде и подсказке карты, хоким/менеджер): геометрия сопоставлена автоматически; строчная, одно слово",
  },
  tierScored: {
    id: "datamap.tier.scored",
    defaultMessage: "с оценкой",
    description:
      "Ярус принятой связи «фича NGIS ↔ контур» (легенда и подсказка карты): сопоставление принято по числовой оценке совпадения; строчная, коротко",
  },
  tierGroup: {
    id: "datamap.tier.group",
    defaultMessage: "групповой",
    description:
      "Ярус принятой связи «фича NGIS ↔ контур» (легенда и подсказка карты): контур собран из нескольких фич; строчная, одно слово",
  },
  tierMerge: {
    id: "datamap.tier.merge",
    defaultMessage: "объединяющий",
    description:
      "Ярус принятой связи «фича NGIS ↔ контур» (легенда и подсказка карты): одна фича показывает несколько контуров; строчная, одно слово",
  },
  tierMn: {
    id: "datamap.tier.mn",
    defaultMessage: "связная M:N-компонента",
    description:
      "Ярус принятой связи «фича NGIS ↔ контур» (легенда и подсказка карты): несколько фич связаны с несколькими контурами; M:N — «многие ко многим», оставить как есть",
  },
  relIdentity: {
    id: "datamap.relation.identity",
    defaultMessage: "фича = контур",
    description:
      "Отношение геометрии в подсказке и легенде карты: одна фича NGIS соответствует ровно одному контуру; знак «=» оставить",
  },
  relPartOf: {
    id: "datamap.relation.partOf",
    defaultMessage: "фича = часть контура",
    description:
      "Отношение геометрии в подсказке и легенде карты: фича NGIS — только часть одного контура",
  },
  relMergedInto: {
    id: "datamap.relation.mergedInto",
    defaultMessage: "одна фича = несколько контуров",
    description:
      "Отношение геометрии в подсказке и легенде карты: один полигон показывает сразу несколько контуров",
  },
  relMToN: {
    id: "datamap.relation.mToN",
    defaultMessage: "несколько фич = несколько контуров",
    description:
      "Отношение геометрии в подсказке и легенде карты: связь «многие ко многим» между фичами NGIS и контурами",
  },
  sharedNote: {
    id: "datamap.tier.sharedNote",
    defaultMessage: "эта геометрия относится к нескольким контурам сразу",
    description:
      "Пояснение в подсказке под курсором и в легенде карты для полигона, который принадлежит нескольким контурам; строчная, без точки; звучит одинаково в легенде и подсказке",
  },
});

/**
 * Токены обводки. Перечислены типом, чтобы полотно резолвило ровно их.
 *
 * Медные ступени названы токенами КАНДИДАТНОСТИ (ADR-0009), а не токенами
 * бренда: медь в продукте означает одно — связь не один-к-одному
 * (несопоставленная связь, кандидатная геометрия). Ярусы `group`, `merge` и
 * `mn_group` — ровно этот случай: у них одна геометрия отвечает за несколько
 * контуров либо контур собран из нескольких фич. Значения ступеней прежние,
 * поменялось имя смысла.
 */
export type TierColorToken =
  | "--muted"
  | "--ink"
  | "--data-candidate"
  | "--data-candidate-line"
  | "--brown-ink";

export interface TierEntry {
  /** Код яруса, как он приходит с сервера. */
  tier: string;
  /** Отношение канона, в котором этот ярус приезжает ВСЕГДА. */
  relation: string;
  /** Подпись яруса. */
  label: string;
  /** Подпись отношения — что именно связь утверждает о геометрии. */
  relationLabel: string;
  /**
   * Одна фича этого яруса показывает НЕСКОЛЬКО контуров. Не украшение: от
   * этого зависит, читается ли полигон как «часть одного контура» или как
   * «общая геометрия нескольких», а это противоположные утверждения.
   */
  multiContour: boolean;
  /** Цвет обводки — токеном, не hex: карта берёт значение из дизайн-системы. */
  color: TierColorToken;
  lineWidth: number;
  /** Штриховка maplibre; `null` — сплошная линия. */
  dash: number[] | null;
  /** Класс образца легенды в `map.module.css`. */
  swatch: string;
}

/**
 * Порядок = СТАРШИНСТВО яруса, от самого прямого сопоставления к самому
 * собранному. Тот же порядок объявлен на сервере (`TIER_ORDER` реестра
 * `import_geometry_links`), и он же решает, каким ярусом подписан контур, у
 * которого принятых связей несколько.
 */
export const TIER_REGISTRY: readonly TierEntry[] = [
  {
    tier: "auto",
    relation: "identity",
    get label() {
      return tr(m.tierAuto);
    },
    get relationLabel() {
      return tr(m.relIdentity);
    },
    multiContour: false,
    color: "--muted",
    lineWidth: 0.5,
    dash: null,
    swatch: "tierAuto",
  },
  {
    tier: "scored",
    relation: "identity",
    get label() {
      return tr(m.tierScored);
    },
    get relationLabel() {
      return tr(m.relIdentity);
    },
    multiContour: false,
    color: "--ink",
    lineWidth: 0.7,
    dash: null,
    swatch: "tierScored",
  },
  {
    tier: "group",
    relation: "part_of",
    get label() {
      return tr(m.tierGroup);
    },
    get relationLabel() {
      return tr(m.relPartOf);
    },
    multiContour: false,
    color: "--data-candidate",
    lineWidth: 1,
    dash: [2, 1.5],
    swatch: "tierGroup",
  },
  {
    tier: "merge",
    relation: "merged_into",
    get label() {
      return tr(m.tierMerge);
    },
    get relationLabel() {
      return tr(m.relMergedInto);
    },
    multiContour: true,
    color: "--data-candidate-line",
    lineWidth: 1.2,
    dash: [1, 1.5],
    swatch: "tierMerge",
  },
  {
    tier: "mn_group",
    relation: "m_to_n",
    get label() {
      return tr(m.tierMn);
    },
    get relationLabel() {
      return tr(m.relMToN);
    },
    multiContour: true,
    color: "--brown-ink",
    lineWidth: 1.2,
    dash: [4, 1.5, 1, 1.5],
    swatch: "tierMn",
  },
];

/**
 * Общая формулировка для ярусов, где геометрия принадлежит не одному контуру.
 * Она обязана звучать одинаково в легенде и в подсказке: пользователь видит
 * один и тот же полигон у соседних контуров и должен прочесть это как
 * отношение, а не как ошибку данных.
 */
export const sharedGeometryNote = (): string => tr(m.sharedNote);

/** Оформление яруса, которого в реестре нет. Видимое, но честно чужое. */
export const UNKNOWN_TIER = {
  color: "--gis-missing",
  lineWidth: 1.4,
  dash: [0.5, 2],
  swatch: "tierUnknown",
} as const;

export const TIER_COLOR_TOKENS: readonly TierColorToken[] = [
  ...new Set(TIER_REGISTRY.map((entry) => entry.color)),
];

const BY_TIER = new Map(TIER_REGISTRY.map((entry) => [entry.tier, entry]));

/** Запись реестра по коду яруса; `undefined` — ярус реестру неизвестен. */
export function tierEntry(code: string | null | undefined): TierEntry | undefined {
  return code ? BY_TIER.get(code) : undefined;
}

/** Подписи ярусов — выведены из реестра, а не переписаны вторым словарём. */
export const tierLabels = (): Record<string, string> =>
  Object.fromEntries(TIER_REGISTRY.map((entry) => [entry.tier, entry.label]));

/**
 * Подписи отношений. Собираются тем же обходом: отношение появляется в
 * словаре ровно тогда, когда появился ярус, который его приносит.
 */
export const relationLabels = (): Record<string, string> =>
  Object.fromEntries(
    TIER_REGISTRY.map((entry) => [entry.relation, entry.relationLabel]),
  );

/** Отношения, при которых геометрия принадлежит нескольким контурам. */
export const MULTI_CONTOUR_RELATIONS: readonly string[] = [
  ...new Set(
    TIER_REGISTRY.filter((entry) => entry.multiContour).map(
      (entry) => entry.relation,
    ),
  ),
];

/** Порядок ярусов в легенде — он же старшинство. */
export const TIER_ORDER: readonly string[] = TIER_REGISTRY.map(
  (entry) => entry.tier,
);

export interface TierLineLayer {
  id: string;
  /** Ярус слоя; `null` — слой «всё остальное». */
  tier: string | null;
  filter: FilterSpecification;
  paint: {
    "line-color": string;
    "line-width": number;
    "line-dasharray"?: number[];
  };
}

/**
 * Слои обводки для полотна: по слою на ярус плюс слой «незнакомый ярус».
 *
 * Слой на ярус, а не одно выражение, потому что `line-dasharray` в maplibre не
 * бывает data-driven. Последний слой — не перестраховка: пока его не было,
 * контур с ярусом вне списка оставался вовсе без собственной черты, и это
 * невозможно было заметить глазом — полигон-то рисуется заливкой.
 */
export function tierLineLayers(
  color: Record<TierColorToken | "--gis-missing", string>,
): TierLineLayer[] {
  const layers: TierLineLayer[] = TIER_REGISTRY.map((entry) => ({
    id: `contours-line-${entry.tier}`,
    tier: entry.tier,
    filter: ["==", ["get", "tier"], entry.tier] as unknown as FilterSpecification,
    paint: {
      "line-color": color[entry.color],
      "line-width": entry.lineWidth,
      ...(entry.dash ? { "line-dasharray": entry.dash } : {}),
    },
  }));

  layers.push({
    id: "contours-line-unknown",
    tier: null,
    filter: [
      "!",
      ["in", ["get", "tier"], ["literal", [...TIER_ORDER]]],
    ] as unknown as FilterSpecification,
    paint: {
      "line-color": color["--gis-missing"],
      "line-width": UNKNOWN_TIER.lineWidth,
      "line-dasharray": [...UNKNOWN_TIER.dash],
    },
  });

  return layers;
}
